// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

/*
    xng-change:
    * Update the structure and style to match our global styles.
*/
import { LocationModel } from "@insite/client-framework/Common/Hooks/useLocationFilterSearch";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import { PaginationModel } from "@insite/client-framework/Types/ApiModels";
import AddressInfoCondensedDisplay, {
    AddressInfoCondensedDisplayStyles,
} from "@insite/content-library/Components/AddressInfoCondensedDisplay";
import DistanceDisplay, { DistanceUnitOfMeasure } from "@insite/content-library/Components/DistanceDisplay";
import GoogleMapsDirectionLink from "@insite/content-library/Components/GoogleMapsDirectionLink";
import LocationContentLink from "@insite/content-library/Components/LocationContentLink";
import LocationPagination from "@insite/content-library/Components/LocationPagination";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import { PaginationPresentationProps } from "@insite/mobius/Pagination";
import Radio, { RadioComponentProps } from "@insite/mobius/Radio";
import RadioGroup, { RadioGroupProps } from "@insite/mobius/RadioGroup";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Pluralize from "@monsma/XDL/Components/Common/Pluralize";
import Notify from "@monsma/XDL/Components/Notify";
import { screen } from "@monsma/XDL/Helpers";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    extendedStyles?: LocationSearchFormStyles;
    onSearch: (searchGeoLocationFilter: string, searchLocationFilter: string, radiusSearch?: number) => void;
    onLocationSelected: (location: LocationModel) => void;
    locationKnown: boolean;
    showSelectedLocation: boolean;
    getLocationNumberFromPagination: (locationIndex: number) => number;
    openLocationContentDisplay: (location: LocationModel) => void;
    resultCount: number;
    locationSearchFilter: string;
    radiusSearchFilter?: number;
    geoLocationSearchText: string;
    showGeoLocationErrorMessage: boolean;
    geoLocationErrorMessage: React.ReactNode;
    isLoading: boolean;
    distanceUnitOfMeasure: DistanceUnitOfMeasure;
    setDistanceUnitOfMeasure: (uom: DistanceUnitOfMeasure) => void;
    locations: LocationModel[];
    locationsPagination: PaginationModel | undefined;
    setPage: (page: number) => void;
    setPageSize: (pageSize: number) => void;
    siteMessageResultsErrorMessage: React.ReactNode;
}

const mapStateToProps = (state: ApplicationState) => ({
    session: state.context.session,
    settings: getSettingsCollection(state),
});

const mapDispatchToProps = {};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface LocationSearchFormStyles {
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    form?: InjectableCss;
    searchContainer?: InjectableCss;
    searchInputRow?: InjectableCss;
    searchInputGridContainer: GridContainerProps;
    searchGeoLocationGridItem?: GridItemProps;
    searchGeoLocationText?: TextFieldPresentationProps;
    searchLocationsGridItem?: GridItemProps;
    searchLocationsText?: TextFieldPresentationProps;
    searchRadiusGridItem?: GridItemProps;
    searchRadiusText?: TextFieldPresentationProps;
    searchButtonGridItem?: GridItemProps;
    searchButton?: ButtonPresentationProps;
    searchResultsTextGridItem?: GridItemProps;
    searchResultsText?: TypographyPresentationProps;
    distanceUnitOfMeasureGridItem?: GridItemProps;
    distanceUnitOfMeasureRadioGroup?: RadioGroupProps;
    distanceUnitOfMeasureImperialRadio?: RadioComponentProps;
    distanceUnitOfMeasureMetricRadio?: RadioComponentProps;
    siteMessageGridItem?: GridItemProps;
    siteMessageGeocodeErrorText?: TypographyPresentationProps;
    siteMessageResultsErrorText?: TypographyPresentationProps;
    searchResultGridItem?: GridItemProps;
    searchResultRow?: InjectableCss;
    searchResultGridContainer?: GridContainerProps;
    searchResultLocationDetailsGridContainer?: GridContainerProps;
    searchResultInformationGridItem?: GridItemProps;
    searchResultInformationGridContainer?: GridContainerProps;
    searchResultDisplayNumberGridItem?: GridItemProps;
    locationDisplayNumberText?: TypographyPresentationProps;
    searchResultLocationGridItem?: GridItemProps;
    locationNameText?: TypographyPresentationProps;
    locationNameLink?: LinkPresentationProps;
    searchResultAddressInfoOffsetGridItem?: GridItemProps;
    searchResultAddressInfoGridItem?: GridItemProps;
    locationAddressInfoCondensed?: AddressInfoCondensedDisplayStyles;
    searchResultLinksAndDistanceContainer?: GridContainerProps;
    searchResultPhoneOffsetGridItem?: GridItemProps;
    searchResultPhoneGridItem?: GridItemProps;
    searchResultPhoneLink?: LinkPresentationProps;
    searchResultLinksOffsetGridItem?: GridItemProps;
    searchResultContentGridItem?: GridItemProps;
    locationContentLink?: LinkPresentationProps;
    searchResultGoogleMapsDirectionLinkGridItem?: GridItemProps;
    locationGoogleMapsDirectionLink?: LinkPresentationProps;
    searchResultDistanceGridItem?: GridItemProps;
    locationDistanceDisplayText?: TypographyPresentationProps;
    searchResultDividerGridItem?: GridItemProps;
    searchResultDivider?: InjectableCss;
    paginationRow?: GridItemProps;
    pagination?: PaginationPresentationProps;
    searchContent?: InjectableCss;
    searchGridContainer?: GridContainerProps;
    locationSearchResultsTextGridItem?: GridItemProps;
    paginationGridItem?: GridItemProps;
    searchResultlocationGridItem?: GridItemProps;
}

export const locationSearchFormStyles: LocationSearchFormStyles = {
    centeringWrapper: {
        css: css`
            width: 100%;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
    form: {
        css: css`
            width: 100%;
        `,
    },
    searchContainer: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            flex: 1 1 100%;
            margin-bottom: 25px;
            padding: 7.5px;
        `,
    },
    searchInputGridContainer: {
        gap: 15,
    },
    searchGeoLocationGridItem: {
        width: [12, 12, 6, 5, 4],
    },
    searchLocationsGridItem: {
        width: [12, 12, 4, 4, 3],
    },
    searchRadiusGridItem: {
        width: [12, 12, 12, 12, 4],
    },
    searchButtonGridItem: {
        width: [12, 12, 2, 3, 5],
    },
    searchButton: {
        css: css`
            margin-top: 24px;
            ${screen("lte", "md")} {
                margin-top: 0;
            }
        `,
    },
    searchContent: {
        css: css`
            flex: 0 0 450px;
            padding: 7.5px;
            ${screen("lte", "lg")} {
                flex-grow: 1;
            }
            ${screen("lte", "md")} {
                flex: 1 1 100%;
            }
        `,
    },
    locationSearchResultsTextGridItem: {
        width: 12,
        align: "middle",
        css: css`
            align-items: center;
            justify-content: space-between;
            padding-bottom: 20px;
        `,
    },
    searchResultsText: {
        weight: "bold",
        css: css`
            .total-label {
                font-weight: normal;
            }
        `,
    },
    searchResultsTextGridItem: {
        width: 4,
    },
    distanceUnitOfMeasureGridItem: {
        width: 8,
        align: "middle",
    },
    distanceUnitOfMeasureRadioGroup: {},
    siteMessageGridItem: {
        width: 12,
        css: css`
            &:empty {
                display: none;
            }
            > div {
                width: 100%;
            }
        `,
    },
    siteMessageGeocodeErrorText: {
        color: "danger",
        weight: "bold",
    },
    siteMessageResultsErrorText: {
        color: "danger",
        weight: "bold",
    },
    searchResultGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
            height: 430px;
            overflow: auto;
            ${screen("lte", "sm")} {
                height: auto;
            }
        `,
    },
    searchResultGridContainer: {
        gap: 0,
        css: css`
            flex-grow: unset;
        `,
    },
    searchResultLocationDetailsGridContainer: {
        gap: 10,
    },
    searchResultDisplayNumberGridItem: {
        width: 1,
        css: css`
            justify-content: center;
        `,
    },
    locationDisplayNumberText: {
        color: "primary",
        weight: "bold",
        size: 14,
        lineHeight: "20px",
        css: css`
            margin-bottom: 15px;
        `,
    },
    searchResultInformationGridItem: {
        width: 11,
    },
    searchResultInformationGridContainer: {
        gap: 10,
    },
    searchResultLocationGridItem: {
        width: 11,
    },
    locationNameText: {
        color: "primary",
        weight: "bold",
        ellipsis: true,
    },
    locationNameLink: {
        typographyProps: {
            weight: "bold",
            ellipsis: true,
            size: 14,
            lineHeight: "20px",
        },
        css: css`
            margin-bottom: 15px;
        `,
    },
    searchResultAddressInfoOffsetGridItem: {
        width: 1,
        css: css`
            justify-content: center;
        `,
    },
    searchResultAddressInfoGridItem: {
        width: 11,
    },
    searchResultPhoneOffsetGridItem: {
        width: 1,
    },
    searchResultPhoneGridItem: {
        width: 11,
    },
    searchResultPhoneLink: {
        typographyProps: {
            size: 13,
            lineHeight: "22px",
        },
    },
    searchResultLinksAndDistanceContainer: {
        gap: 0,
        css: css`
            width: 100%;
            padding: 0;
            margin: 0;
        `,
    },
    searchResultLinksOffsetGridItem: {
        width: 1,
    },
    searchResultContentGridItem: {
        width: 3,
        css: css`
            padding-top: 10px;
        `,
    },
    searchResultGoogleMapsDirectionLinkGridItem: {
        width: 3,
        css: css`
            padding-top: 10px;
        `,
    },
    searchResultDistanceGridItem: {
        width: 5,
        css: css`
            padding-top: 10px;
        `,
    },
    searchResultDividerGridItem: {
        width: 12,
    },
    searchResultDivider: {
        css: css`
            margin: 20px 0;
            border: 1px solid ${getColor("common.ui12")};
            width: 100%;
        `,
    },
    paginationGridItem: {
        width: 12,
        css: css`
            justify-content: center;
            nav {
                justify-content: center;
            }
        `,
    },
    locationAddressInfoCondensed: {
        wrapper: {
            css: css`
                font-size: 13px;
                line-height: 22px;
            `,
        },
    },
    locationDistanceDisplayText: {
        size: 12,
        lineHeight: "15px",
    },
    searchGridContainer: {
        gap: 0,
    },
};

const StyledForm = getStyledWrapper("form");
const StyledHr = getStyledWrapper("hr");

const Container = StyledWrapper;
const ContainerCell = StyledWrapper;

const LocationSearchForm: React.FC<Props> = ({
    extendedStyles,
    onSearch,
    onLocationSelected,
    locationKnown,
    getLocationNumberFromPagination,
    openLocationContentDisplay,
    resultCount,
    locationSearchFilter,
    radiusSearchFilter,
    geoLocationSearchText,
    showGeoLocationErrorMessage,
    geoLocationErrorMessage,
    isLoading,
    distanceUnitOfMeasure,
    setDistanceUnitOfMeasure,
    locations,
    locationsPagination,
    setPage,
    setPageSize,
    siteMessageResultsErrorMessage,
}) => {
    const [styles] = React.useState(() => mergeToNew(locationSearchFormStyles, extendedStyles));
    const [searchLocationFilter, setSearchLocationFilter] = React.useState(locationSearchFilter);
    const [searchRadiusFilter, setSearchRadiusFilter] = React.useState(radiusSearchFilter);
    const [searchGeoLocationFilter, setSearchGeoLocationFilter] = React.useState(geoLocationSearchText);
    React.useEffect(() => {
        setSearchLocationFilter(locationSearchFilter);
    }, [locationSearchFilter]);
    React.useEffect(() => {
        setSearchRadiusFilter(radiusSearchFilter);
    }, [radiusSearchFilter]);
    React.useEffect(() => {
        setSearchGeoLocationFilter(geoLocationSearchText);
    }, [geoLocationSearchText]);

    const handleDistanceUnitOfMeasureChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value as DistanceUnitOfMeasure;
        setDistanceUnitOfMeasure(value);
    };

    const generateOpenLocationContentFor = (location: LocationModel) => {
        return () => openLocationContentDisplay(location);
    };

    const generateLocationSelectClickHandler = (
        event: React.MouseEvent<Element, MouseEvent>,
        location: LocationModel,
    ) => {
        event.preventDefault();
        onLocationSelected(location);
    };

    const isNotFinalResult = (resultIndex: number) => {
        if (!locationsPagination) {
            return false;
        }
        return locationsPagination.pageSize > resultIndex + 1;
    };
    const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (!isLoading) {
            locationsPagination && setPage(1);
            onSearch(searchGeoLocationFilter, searchLocationFilter, searchRadiusFilter);
        }
    };
    const handleSearchGeoLocationChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchGeoLocationFilter(event.target.value);
    };
    const handleSearchLocationsChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchLocationFilter(event.target.value);
    };
    const handleSearchRadiusChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchRadiusFilter(Number(event.target.value));
    };

    return (
        <>
            <StyledWrapper {...styles.searchContainer}>
                <StyledForm {...styles.form} onSubmit={handleSubmit} noValidate>
                    <GridContainer {...styles.searchInputGridContainer}>
                        <GridItem {...styles.searchGeoLocationGridItem}>
                            <TextField
                                id="searchGeoLocation"
                                {...styles.searchGeoLocationText}
                                label={translate("Search by Postal Code, Province or Country")}
                                value={searchGeoLocationFilter}
                                onChange={handleSearchGeoLocationChanged}
                                placeholder={translate("Search by Postal Code, Province or Country")}
                                data-test-selector="locationSearchForm_searchGeoLocation"
                                error={showGeoLocationErrorMessage ? geoLocationErrorMessage : undefined}
                            />
                        </GridItem>
                        <GridItem {...styles.searchLocationsGridItem}>
                            <TextField
                                id="searchLocations"
                                {...styles.searchLocationsText}
                                label={translate("Search for Location (Optional)")}
                                value={searchLocationFilter}
                                onChange={handleSearchLocationsChanged}
                                placeholder={translate("Search for Location (Optional)")}
                                data-test-selector="locationSearchForm_searchLocations"
                            />
                        </GridItem>
                        <GridItem {...styles.searchRadiusGridItem}>
                            <TextField
                                id="searchRadius"
                                {...styles.searchRadiusText}
                                label={translate("Search Radius")}
                                value={searchRadiusFilter}
                                onChange={handleSearchRadiusChanged}
                                type="number"
                                min={0}
                                placeholder={translate("Search Radius")}
                            />
                        </GridItem>
                        <GridItem {...styles.searchButtonGridItem}>
                            <Button
                                {...styles.searchButton}
                                type="submit"
                                data-test-selector="locationSearchForm_searchButton"
                            >
                                {translate("Search")}
                            </Button>
                        </GridItem>
                    </GridContainer>
                </StyledForm>
            </StyledWrapper>
            <StyledWrapper {...styles.searchContent}>
                <GridContainer {...styles.searchGridContainer}>
                    <GridItem {...styles.siteMessageGridItem}>
                        {!isLoading && !locationKnown && geoLocationSearchText.length > 0 && (
                            <Notify
                                messageType="danger"
                                hideCloseButton
                                body={siteMessage("PickUpLocation_GeocodeErrorMessage")}
                            />
                        )}
                        {!isLoading && locationKnown && locations.length === 0 && siteMessageResultsErrorMessage && (
                            <Notify
                                messageType="danger"
                                hideCloseButton
                                data-test-selector="locationSearchForm_siteErrorMessage"
                                body={siteMessageResultsErrorMessage}
                            />
                        )}
                    </GridItem>
                    <GridItem {...styles.locationSearchResultsTextGridItem}>
                        <Typography {...styles.searchResultsText}>
                            <Pluralize
                                total={resultCount}
                                pluralLabel={translate("Results")}
                                singularLabel={translate("Result")}
                                displayZeroResult
                            />
                        </Typography>
                        <RadioGroup
                            {...styles.distanceUnitOfMeasureRadioGroup}
                            value={distanceUnitOfMeasure}
                            onChangeHandler={handleDistanceUnitOfMeasureChange}
                            className="inline-group"
                        >
                            <Radio {...styles.distanceUnitOfMeasureImperialRadio} value="Imperial">
                                {translate("mi")}
                            </Radio>
                            <Radio {...styles.distanceUnitOfMeasureMetricRadio} value="Metric">
                                {translate("km")}
                            </Radio>
                        </RadioGroup>
                    </GridItem>
                    {isLoading && locations.length === 0 && (
                        <GridItem {...styles.searchResultGridItem}>
                            <StyledWrapper {...styles.centeringWrapper}>
                                <LoadingSpinner {...styles.spinner} />
                            </StyledWrapper>
                        </GridItem>
                    )}
                    <GridItem {...styles.searchResultGridItem}>
                        {locations.length > 0 && (
                            <>
                                {locations.map((location, index) => (
                                    <GridContainer
                                        {...styles.searchResultGridContainer}
                                        data-test-selector="locationSearchForm_location"
                                        data-test-key={`${location.id}`}
                                        key={location.id}
                                    >
                                        <GridItem {...styles.searchResultDisplayNumberGridItem}>
                                            <Typography {...styles.locationDisplayNumberText}>
                                                {getLocationNumberFromPagination(index)}
                                            </Typography>
                                        </GridItem>
                                        <GridItem {...styles.searchResultLocationGridItem}>
                                            <Link
                                                {...styles.locationNameLink}
                                                onClick={(event: React.MouseEvent<Element, MouseEvent>) =>
                                                    generateLocationSelectClickHandler(event, location)
                                                }
                                                data-test-selector="findLocationModal_locationSelect"
                                                data-markerNumber={index + 1}
                                            >
                                                {location.name}
                                            </Link>
                                        </GridItem>
                                        <GridItem {...styles.searchResultAddressInfoOffsetGridItem}>
                                            {/* Layout Indentation */}
                                        </GridItem>
                                        <GridItem {...styles.searchResultAddressInfoGridItem}>
                                            <AddressInfoCondensedDisplay
                                                {...styles.locationAddressInfoCondensed}
                                                {...location}
                                            />
                                        </GridItem>
                                        <GridItem {...styles.searchResultPhoneOffsetGridItem}>
                                            {/* Layout Indentation */}
                                        </GridItem>
                                        {location.phone && (
                                            <>
                                                <GridItem {...styles.searchResultPhoneGridItem}>
                                                    <Link
                                                        href={`tel:${location.phone}`}
                                                        {...styles.searchResultPhoneLink}
                                                        onClick={(
                                                            e: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
                                                        ) => {
                                                            e.defaultPrevented = true;
                                                        }}
                                                        aria-label={translate("Tel: {0}", location.phone)}
                                                    >
                                                        {location.phone}
                                                    </Link>
                                                </GridItem>
                                                <GridItem {...styles.searchResultLinksOffsetGridItem}>
                                                    {/* Layout Indentation */}
                                                </GridItem>
                                            </>
                                        )}
                                        <GridItem {...styles.searchResultDistanceGridItem}>
                                            <DistanceDisplay
                                                {...styles.locationDistanceDisplayText}
                                                distance={location.distance}
                                                unitOfMeasure={distanceUnitOfMeasure}
                                            />
                                        </GridItem>
                                        <GridItem {...styles.searchResultGoogleMapsDirectionLinkGridItem}>
                                            <GoogleMapsDirectionLink
                                                {...styles.locationGoogleMapsDirectionLink}
                                                {...location}
                                            />
                                        </GridItem>
                                        {location.htmlContent && (
                                            <GridItem {...styles.searchResultContentGridItem}>
                                                <LocationContentLink
                                                    {...styles.locationContentLink}
                                                    location={location}
                                                    onOpenLocationContent={generateOpenLocationContentFor(location)}
                                                />
                                            </GridItem>
                                        )}
                                        {isNotFinalResult(index) && (
                                            <GridItem {...styles.searchResultDividerGridItem}>
                                                <StyledHr {...styles.searchResultDivider} />
                                            </GridItem>
                                        )}
                                    </GridContainer>
                                ))}
                            </>
                        )}
                    </GridItem>
                    <GridItem {...styles.paginationGridItem}>
                        <LocationPagination
                            {...styles.pagination}
                            locations={locations}
                            locationsPagination={locationsPagination}
                            setPage={setPage}
                            setPageSize={setPageSize}
                        />
                    </GridItem>
                </GridContainer>
            </StyledWrapper>
        </>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(LocationSearchForm);
